import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../components';
import DarkModeToggle from '../components/DarkModeToggle';
import { apiClient } from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { broadcastLogin, waitForAcknowledgement } from '../lib/useAuthSync';

export default function VerifyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setWorkspace } = useAuthStore();
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setError(t('auth.verify.invalidToken'));
        setIsVerifying(false);
        return;
      }

      try {
        // Use encodeURIComponent to properly encode the token in the URL
        const response = await apiClient.get(`/auth/magic-link/verify?token=${encodeURIComponent(token)}`);

        console.log('[VERIFY] Magic link verification response:', response.data);

        // Extract user and workspace from response
        const data = response.data.data || response.data;
        const { user, workspace } = data;

        console.log('[VERIFY] Setting auth state:', { user, workspace });

        // Update auth store with user and workspace
        if (user) {
          setUser(user);
        }
        if (workspace) {
          setWorkspace(workspace);
        }

        // Success - show success message and broadcast to other tabs
        setSuccess(true);
        broadcastLogin();

        // Wait for acknowledgement from other tabs (500ms timeout)
        const hasOtherTabs = await waitForAcknowledgement(500);

        console.log('Has other tabs listening:', hasOtherTabs);

        if (!hasOtherTabs) {
          // Single-tab scenario - no other tabs listening
          // Show redirecting state and redirect after 1 second
          console.log('No other tabs detected, will redirect in 1 second');
          setIsRedirecting(true);
          setTimeout(() => {
            console.log('Redirecting to dashboard...');
            if (workspace && workspace.slug) {
              navigate(`/${workspace.slug}/dashboard`);
            } else {
              navigate('/login');
            }
          }, 1000);
        } else {
          console.log('Other tabs detected, letting them handle redirect');
        }
        // If hasOtherTabs = true, the other tab will handle the redirect
        // This tab stays on success screen for user to close
      } catch (err: any) {
        console.error('Magic link verification error:', err);
        console.log('Token from URL:', token);
        console.log('Error response:', err.response?.data);
        setError(
          err.response?.data?.message || t('auth.verify.error')
        );
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [searchParams, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 p-4 transition-colors duration-300">
      <Card className="w-full max-w-md relative">
        <DarkModeToggle />

        <div className="mb-8 text-center">
          <img
            src="/logo.png"
            alt={t('app.name')}
            className="h-24 mx-auto mb-6 dark:hidden"
          />
          <img
            src="/logo-white.png"
            alt={t('app.name')}
            className="h-24 mx-auto mb-6 hidden dark:block"
          />
          <h1 className="text-3xl font-bold mb-2 dark:text-gray-100">
            {isVerifying
              ? t('auth.verify.verifying')
              : success
                ? t('auth.verify.successTitle')
                : t('auth.verify.title')}
          </h1>
        </div>

        {isVerifying ? (
          <div className="space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 border-4 border-gray-200 dark:border-gray-700 border-t-indigo-600 dark:border-t-indigo-400 rounded-full animate-spin"></div>
            </div>
            <p className="text-center text-gray-600 dark:text-gray-400">
              {t('auth.verify.pleaseWait')}
            </p>
          </div>
        ) : success ? (
          <div className="space-y-6">
            <div className="p-6 bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg">
              <div className="flex items-start gap-4">
                <svg
                  className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="w-full">
                  <h3 className="font-semibold text-green-900 dark:text-green-200 mb-2">
                    {t('auth.verify.successTitle')}
                  </h3>
                  <p className="text-sm text-green-800 dark:text-green-300 mb-3">
                    {t('auth.verify.successMessage')}
                  </p>
                  {isRedirecting ? (
                    <div className="pt-3 border-t border-green-200 dark:border-green-700">
                      <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                        {t('auth.verify.redirectingNow')}
                      </p>
                    </div>
                  ) : (
                    <div className="pt-3 border-t border-green-200 dark:border-green-700">
                      <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                        {t('auth.verify.canCloseTab')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {isRedirecting && (
              <div className="flex justify-center">
                <div className="w-8 h-8 border-4 border-green-200 dark:border-green-700 border-t-green-600 dark:border-t-green-400 rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        ) : error ? (
          <div className="space-y-6">
            <div className="p-6 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
              <div className="flex items-start gap-4">
                <svg
                  className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h3 className="font-semibold text-red-900 dark:text-red-200 mb-1">
                    {t('auth.verify.errorTitle')}
                  </h3>
                  <p className="text-sm text-red-800 dark:text-red-300">
                    {error}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <a
                href="/magic-link"
                className="inline-block text-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
              >
                {t('auth.verify.requestNew')}
              </a>
              <a
                href="/login"
                className="text-sm text-center font-medium text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white hover:underline"
              >
                {t('auth.verify.backToLogin')}
              </a>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
